import DownloadIcon from "assets/images/download.svg";
import PrintIcon from "assets/images/print.svg";
import ShareIcon from "assets/images/share.svg";
import Volume from "assets/images/volume.svg";
import ShareDialog from "component/news/ShareDialog.jsx";
import Grains from '../../assets/images/grains.jpg'
import { apiBaseUrl } from "config/api";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Chart from "react-apexcharts";
import ReportDetailPanel from "component/reports/ReportDetailPanel";
import CustomizeArticleReport from "component/reports/CustomizeArticleReport";
import { useParams, useSearchParams } from "react-router-dom";
import { reportService } from "../../services/reportService";
import { interactionService } from "../../services";
import { getSelectedLanguage, normalizeLanguageCode } from "utils/language";
import { getPublishedPrecision } from "utils/formatReportDate";

const slugifyAnchor = (value, fallback) => {
    const raw = (value || "").toString().trim().toLowerCase();
    const slug = raw
        .replace(/[^a-z0-9\s-]/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-")
        .replace(/^-|-$/g, "");
    return slug || fallback;
};

const ReportView = () => {
    const { t, i18n } = useTranslation();
    const [open, setOpen] = useState(false);
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const [report, setReport] = useState(null);
    const [isCustomReport, setIsCustomReport] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [customization, setCustomization] = useState(null);
    const [enhancement, setEnhancement] = useState(null);
    const [enhancePrompt, setEnhancePrompt] = useState("");
    const [enhancing, setEnhancing] = useState(false);
    const [enhanceError, setEnhanceError] = useState("");
    const [enhanceStatus, setEnhanceStatus] = useState("");
    const [loadingHint, setLoadingHint] = useState("");
    const selectedLanguage = normalizeLanguageCode(
        i18n.resolvedLanguage || i18n.language || getSelectedLanguage()
    );
    const forceCustomReport = (searchParams.get("type") || "").toLowerCase() === "custom";

    useEffect(() => {
        if (!id) return;
        const run = async () => {
            setLoading(true);
            setLoadingHint("");
            setError(null);
            setReport(null);
            setIsCustomReport(false);
            try {
                if (forceCustomReport) {
                    const customData = await reportService.getCustomReport(id, { language: selectedLanguage });
                    setReport(customData);
                    setIsCustomReport(true);
                    if ((customData?.status || "").toLowerCase() === "generating") {
                        const remainingSeconds = Number(customData?.metadata_json?.remaining_seconds);
                        const etaText = Number.isFinite(remainingSeconds) && remainingSeconds > 0
                            ? `~${Math.ceil(remainingSeconds / 60)} min`
                            : (customData?.metadata_json?.eta_hint || "2-5 min");
                        setLoadingHint(`${t("reports.generating")} (${etaText})`);
                    }
                    interactionService.trackView('custom_report', id);
                } else {
                    const data = await reportService.getReport(id, { language: selectedLanguage });
                    setReport(data);
                    setIsCustomReport(false);
                    interactionService.trackView('report', id);
                }
            } catch (e) {
                const status = e?.status || e?.response?.status;
                if (status === 404 && !forceCustomReport) {
                    try {
                        const customData = await reportService.getCustomReport(id, { language: selectedLanguage });
                        setReport(customData);
                        setIsCustomReport(true);
                        if ((customData?.status || "").toLowerCase() === "generating") {
                            const remainingSeconds = Number(customData?.metadata_json?.remaining_seconds);
                            const etaText = Number.isFinite(remainingSeconds) && remainingSeconds > 0
                                ? `~${Math.ceil(remainingSeconds / 60)} min`
                                : (customData?.metadata_json?.eta_hint || "2-5 min");
                            setLoadingHint(`${t("reports.generating")} (${etaText})`);
                        }
                        interactionService.trackView('custom_report', id);
                    } catch (customErr) {
                        setError(customErr?.message || t("reports.error.loadSingle"));
                    }
                } else if (status === 404 && forceCustomReport) {
                    try {
                        const data = await reportService.getReport(id, { language: selectedLanguage });
                        setReport(data);
                        setIsCustomReport(false);
                        interactionService.trackView('report', id);
                    } catch (reportErr) {
                        setError(reportErr?.message || t("reports.error.loadSingle"));
                    }
                } else {
                    setError(e?.message || t("reports.error.loadSingle"));
                }
            } finally {
                setLoading(false);
            }
        };
        run();
    }, [forceCustomReport, id, selectedLanguage, t]);

    useEffect(() => {
        setCustomization(null);
        setEnhancement(null);
        setEnhancePrompt("");
        setEnhanceError("");
        setEnhanceStatus("");
    }, [id]);

    useEffect(() => {
        if (!id || !isCustomReport) return;
        if ((report?.status || "").toLowerCase() !== "generating") return;
        let stopped = false;
        const poll = async () => {
            try {
                const customData = await reportService.getCustomReport(id, { language: selectedLanguage });
                if (stopped) return;
                setReport(customData);
                if ((customData?.status || "").toLowerCase() !== "generating") {
                    setLoadingHint("");
                    setLoading(false);
                    return;
                }
                const remainingSeconds = Number(customData?.metadata_json?.remaining_seconds);
                const etaText = Number.isFinite(remainingSeconds) && remainingSeconds > 0
                    ? `~${Math.ceil(remainingSeconds / 60)} min`
                    : (customData?.metadata_json?.eta_hint || "2-5 min");
                setLoadingHint(`${t("reports.generating")} (${etaText})`);
                setLoading(false);
            } catch {
                // keep current state; polling will retry
            }
        };
        const interval = setInterval(poll, 7000);
        return () => {
            stopped = true;
            clearInterval(interval);
        };
    }, [id, isCustomReport, report?.status, selectedLanguage, t]);

    const resolveImage = useCallback((url) => {
        if (!url) return `${apiBaseUrl("news/placeholder")}`;
        if (url.startsWith("http")) return url;
        const filename = url.split("/").pop();
        return `${apiBaseUrl(`hero-images/${filename}`)}`;
    }, []);

    const parseSectionSource = useCallback((entry, index) => {
        const raw = (entry || "").toString().trim();
        if (!raw) return null;

        const urlMatch = raw.match(/https?:\/\/[^\s\]\)>,]+/i);
        const url = urlMatch ? urlMatch[0] : "";
        const remainder = url ? raw.replace(url, "").replace(/[()\-\u2013\u2014:|]/g, " ").trim() : raw;

        let label = remainder;
        if (!label && url) {
            try {
                const host = new URL(url).hostname.replace(/^www\./i, "");
                label = host.split(".")[0] || host;
            } catch {
                label = `Source ${index + 1}`;
            }
        }
        if (!label) label = `Source ${index + 1}`;

        // Normalize label casing and remove noisy path-like values.
        label = label
            .replace(/[_-]+/g, " ")
            .replace(/\s+/g, " ")
            .trim();

        return {
            label,
            url: url || null,
            key: `${label}-${url || index}`,
        };
    }, []);

    const normalizeDetail = useCallback((opt) => {
        const v = (opt || "").toString().toLowerCase();
        if (v.startsWith("short")) return "short";
        if (v.startsWith("detail")) return "detailed";
        return "original";
    }, []);

    const normalizeStyle = useCallback((opt) => {
        const v = (opt || "").toString().toLowerCase();
        if (v.startsWith("analyt")) return "analytical";
        if (v.startsWith("manage")) return "management";
        return "original";
    }, []);

    const normalizeExtras = useCallback((vals = []) => {
        const allowed = new Set(["quotation", "news", "sources", "graphs", "academical", "comparisons"]);
        return Array.from(
            new Set(
                (vals || [])
                    .map((v) => (v || "").toString().toLowerCase().trim())
                    .filter((v) => allowed.has(v))
            )
        );
    }, []);

    const ensureCustomization = useCallback(async () => {
        if (customization?.id) return customization.id;
        if (!report?.id) throw new Error(t("reports.error.loadSingle"));
        const payload = {
            detail_level: normalizeDetail(report?.detail_level || "original"),
            writing_style: normalizeStyle(report?.writing_style || "original"),
            additional_information: normalizeExtras(report?.additional_information || []),
            language: selectedLanguage,
        };
        const res = await reportService.createCustomization(report.id, payload);
        if (res?.id) {
            setCustomization(res);
            return res.id;
        }
        return null;
    }, [customization, normalizeDetail, normalizeExtras, normalizeStyle, report, selectedLanguage, t]);

    const handleEnhance = useCallback(
        async (label = "enhanced") => {
            if (isCustomReport) {
                setEnhanceError(t("reports.enhance.notAvailableForCustom"));
                return;
            }
            const trimmed = (enhancePrompt || "").trim();
            if (!trimmed) {
                setEnhanceError(t("reports.enhance.enterPrompt"));
                return;
            }
            setEnhancing(true);
            setEnhanceError("");
            setEnhanceStatus("");
            try {
                const customizationId = await ensureCustomization();
                if (!customizationId) {
                    throw new Error(t("reports.enhance.prepareFailed"));
                }
                const res = await reportService.createEnhancement(customizationId, {
                    prompt: trimmed,
                    language: selectedLanguage,
                });
                setEnhancement(res);
                setEnhanceStatus(
                    label === "rewritten"
                        ? t("reports.enhance.statusRewritten")
                        : t("reports.enhance.statusEnhanced")
                );
            } catch (e) {
                setEnhanceError(e?.message || t("reports.enhance.failed"));
            } finally {
                setEnhancing(false);
            }
        },
        [enhancePrompt, ensureCustomization, isCustomReport, selectedLanguage, t]
    );

    const handleCustomizationApplied = useCallback((customData) => {
        if (!customData) return;
        setCustomization(customData);
        setEnhancement(null);
        setEnhanceStatus(t("reports.enhance.customizationApplied"));
    }, [t]);

    const computed = useMemo(() => {
        const sectorNames = Array.isArray(report?.sectors)
            ? report.sectors.map((s) => s?.name || s).filter(Boolean)
            : [t("reports.fallbackSector.tech"), t("reports.fallbackSector.finance")];
        const meta = enhancement?.metadata_json || customization?.metadata_json || report?.metadata_json || {};
        const baseSummary = report?.subtitle || report?.summary || report?.description || report?.ai_summary;
        const displaySummary = enhancement?.response_summary || customization?.summary || baseSummary;
        const aiSummary = enhancement?.response_summary || customization?.summary || report?.ai_summary;
        const contentSections = enhancement?.content_sections || customization?.content_sections || report?.content_sections || [];
        const tableOfContents =
            (enhancement?.metadata_json && enhancement.metadata_json.table_of_contents) ||
            report?.table_of_contents ||
            [];
        const readingTime =
            meta?.read_time_minutes ?? report?.reading_time_minutes ?? report?.reading_minutes ?? meta?.reading_minutes;
        const matchRate = meta?.match_rate ?? report?.match_rate;
        const createdAt = report?.created_at || report?.published_at;
        const title = enhancement?.metadata_json?.title || customization?.title || report?.title || report?.name || t("reports.titleFallback");
        const summary = displaySummary;
        const image = resolveImage(report?.hero_image_url || report?.image_url || report?.cover_image) || Grains;
        const toNumber = (val) => {
            if (val === null || typeof val === "undefined") return undefined;
            const num = typeof val === "string" ? Number(val) : val;
            return Number.isFinite(num) ? num : undefined;
        };
        const numericReadingTime = toNumber(readingTime);
        const numericMatchRate = toNumber(matchRate);

        return {
            sectors: sectorNames.length ? sectorNames : [t("reports.fallbackSector.tech"), t("reports.fallbackSector.finance")],
            title,
            summary,
            aiSummary,
            createdAt,
            readingTime: numericReadingTime,
            contentSections,
            tableOfContents,
            image,
            matchRate: numericMatchRate,
        };
    }, [customization, enhancement, report, resolveImage, t]);

    const sectors = computed.sectors;
    const title = computed.title;
    const summary = computed.summary;
    const aiSummary = computed.aiSummary;
    const created_at = computed.createdAt;
    const reading_time = computed.readingTime;
    const content_sections = computed.contentSections;
    const table_of_contents = computed.tableOfContents;
    const image = computed.image;
    const matchRate = computed.matchRate;
    const isGeneratingCustom = isCustomReport && (report?.status || "").toLowerCase() === "generating";
    const sectionAnchors = useMemo(
        () => (Array.isArray(content_sections) ? content_sections.map((section, idx) => slugifyAnchor(section?.heading, `section-${idx + 1}`)) : []),
        [content_sections]
    );
    const scrollToSection = useCallback((anchor) => {
        if (!anchor) return;
        const el = document.getElementById(`report-section-${anchor}`);
        if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }, []);

    return (
        <div>
            <div className="main__content__box">
                <div className="container-fluid px-0">
                    {error && <div className="text-danger mb-3 px-3">{error}</div>}
                    {(loading || isGeneratingCustom) && (
                        <div className="text-muted mb-3 px-3">
                            {loadingHint || `${t("reports.loadingSingle")} ${t("reports.loadingSingleHint")}`}
                        </div>
                    )}
                    <div className="row g-4">
                        <div
                            className="col-md-7 col-sm-12 col-xl-8"
                        >
                            <div className="main_contennt_box">
                                <div>
                                    <div className="reportViewBannerImgWrap">
                                        <div className="image_box ">
                                            <img
                                                src={image}
                                                alt={t("news.top.imageAlt")}
                                                className="w-100 h-100 object-fit-cover"
                                            />
                                            <div className="report-hero-overlay" />

                                            <h4 className="text-white fw-semibold post_title mb-0">{title}</h4>
                                        </div>
                                    </div>

                                    {/* Action Buttons */}
                                    <div className="action_box">
                                        <span className="fw-light title_box">{t("news.quickAction")}</span>
                                        <div className="action_button_box position-relative">
                                            <button className="action_btn">
                                                <div className="small_cricle_icon">
                                                    <img src={Volume} alt="" />
                                                </div>
                                                <span>{t("reports.actions.read")}</span>
                                            </button>
                                            <button className="action_btn">
                                                <img src={DownloadIcon} alt="" />
                                                <span>{t("news.save")}</span>
                                            </button>
                                            <button className="action_btn" onClick={() => setOpen(!open)}>
                                                <img src={ShareIcon} alt="" />
                                                <span>{t("news.share")}</span>
                                            </button>
                                            <button className="action_btn">
                                                <img src={PrintIcon} alt="" />
                                                <span>{t("news.print")}</span>
                                            </button>
                                            <ShareDialog open={open} />
                                        </div>
                                    </div>
                                </div>

                                <div
                                    className="content_box"
                                    style={{ paddingLeft: "50px", paddingRight: "50px" }}
                                >
                                    {!loading && (
                                      <>
                                        <div className="heading_box py-4">
                                            <h5 className="fw-semibold text-white  news_view_title">
                                                {summary || t("reports.noSummary")}
                                            </h5>
                                            {enhancement && (
                                                <div className="small text-success mt-1">
                                                    {t("reports.enhancedShown")}
                                                </div>
                                            )}
                                        </div>

                                        <div className="mini_detail_box mb-4">
                                            <h4 className="text-white fw-semibold">
                                                {t("reports.aiSummary")}
                                            </h4>
                                            <p className="text-white fw-light mb-0">
                                                {isGeneratingCustom ? t("reports.generating") : (aiSummary || t("reports.noAiSummary"))}
                                            </p>
                                        </div>
                                      </>
                                    )}

                                    {Array.isArray(content_sections) && content_sections.length > 0 && (
                                      <div className="news_detail_description_box">
                                        {content_sections.map((section, idx) => (
                                          <div
                                            key={idx}
                                            id={`report-section-${sectionAnchors[idx] || `section-${idx + 1}`}`}
                                            className="mb-5 pb-3 border-bottom border-secondary"
                                          >
                                            <h4 className="text-white fw-semibold mb-2">{section.heading || t("reports.sectionTitle", { index: idx + 1 })}</h4>
                                            {section.summary && (
                                              <p className="text-white fw-light mb-2">{section.summary}</p>
                                            )}
                                            {section.body && (
                                              <p className="text-white fw-light mb-2">{section.body}</p>
                                            )}
                                            {section.chart && Array.isArray(section.chart.series) && section.chart.series.length > 0 && (
                                              <div className="mb-3 bg_glassEffect p-3 rounded-4">
                                                <Chart
                                                  type={section.chart.type || "line"}
                                                  height={260}
                                                  options={{
                                                    chart: { toolbar: { show: false } },
                                                    stroke: { curve: "smooth" },
                                                    xaxis: { categories: section.chart.categories || section.chart.labels || [] },
                                                    dataLabels: { enabled: false },
                                                    tooltip: { theme: "dark" },
                                                    legend: { show: false },
                                                    grid: { borderColor: "rgba(255,255,255,0.08)" },
                                                  }}
                                                  series={section.chart.series}
                                                />
                                              </div>
                                            )}
                                            {Array.isArray(section.insights) && section.insights.length > 0 && (
                                              <div className="mb-2">
                                                <h6 className="text-white fw-semibold mb-1">{t("reports.insights")}</h6>
                                                <ul className="text-white-50 mb-0 ps-3" style={{ listStyle: "disc" }}>
                                                  {section.insights.map((insight, j) => (
                                                    <li key={j} className="mb-1">{insight}</li>
                                                  ))}
                                                </ul>
                                              </div>
                                            )}
                                            {Array.isArray(section.sources) && section.sources.length > 0 && (
                                              <div className="small text-white-50 mt-2">
                                                <div className="mb-1">{t("reports.sourcesLabel")}:</div>
                                                <div className="d-flex flex-column gap-1">
                                                  {section.sources
                                                    .map((source, sIdx) => parseSectionSource(source, sIdx))
                                                    .filter(Boolean)
                                                    .map((source) => (
                                                      source.url ? (
                                                        <a
                                                          key={source.key}
                                                          href={source.url}
                                                          target="_blank"
                                                          rel="noopener noreferrer"
                                                          className="text-decoration-underline text-white-50"
                                                          title={source.url}
                                                        >
                                                          {source.label}
                                                        </a>
                                                      ) : (
                                                        <span key={source.key} className="text-white-50">
                                                          {source.label}
                                                        </span>
                                                      )
                                                    ))}
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    )}


                                </div>
                            </div>
                        </div>

                        <div
                            className="col-sm-12 col-md-5 col-lg-5 col-xl-4"
                            style={{ maxWidth: "400px", flex: "0 1 400px" }}
                        >
                            <ReportDetailPanel
                                progress={matchRate ?? 70}
                                sectors={sectors}
                                reading_time_minutes={reading_time ?? 3}
                                create_date={created_at || "—"}
                                published_date={isCustomReport ? null : report?.published_at}
                                published_precision={isCustomReport ? "day" : getPublishedPrecision(report)}
                                source_logos={[]}
                                tags={report?.tags || []}
                            />

                            {!isCustomReport && (
                                <CustomizeArticleReport
                                    displayArticleId={report?.id || "report"}
                                    onCustomizationApplied={handleCustomizationApplied}
                                />
                            )}

                            {!isCustomReport && (
                            <div className="content_enhance_wrap bg_glassEffect mt-4">
                                <h6 className="enhance_title">{t("reports.enhance.title")}</h6>
                                <textarea
                                    className="enhance_textarea"
                                    placeholder={t("reports.enhance.placeholder")}
                                    value={enhancePrompt}
                                    onChange={(e) => setEnhancePrompt(e.target.value)}
                                    disabled={enhancing}
                                ></textarea>

                                <div className="enhance_btns">
                                    <button className="btn_enhance" onClick={() => handleEnhance("enhanced")} disabled={enhancing || !enhancePrompt.trim()}>{enhancing ? t("common.working") : t("reports.enhance.enhance")}</button>
                                    <button className="btn_rewrite active" onClick={() => handleEnhance("rewritten")} disabled={enhancing || !enhancePrompt.trim()}>{enhancing ? t("common.working") : t("reports.enhance.rewrite")}</button>
                                </div>
                                {enhanceError && <div className="small text-danger mt-2">{enhanceError}</div>}
                                {enhanceStatus && <div className="small text-success mt-2">{enhanceStatus}</div>}
                            </div>
                            )}

                            <div className="tranding_topic_list">
                                {Array.isArray(table_of_contents) && table_of_contents.length > 0 && (
                                  <>
                                    <ul>
                                      {table_of_contents.map((section, idx) => (
                                        <li key={idx}>
                                          <button
                                            type="button"
                                            className="fw-semibold text-white bg-transparent border-0 p-0 text-start"
                                            onClick={() => scrollToSection(section.anchor || sectionAnchors[idx] || `section-${idx + 1}`)}
                                          >
                                            {section.title || t("reports.sectionTitle", { index: idx + 1 })}
                                          </button>
                                        </li>
                                      ))}
                                    </ul>
                                  </>
                                )}
                            </div>

                            {/*<div className="matching_sector_tabs_box mt-4">
                                <div className="bg_glassEffect pt-4 pb-3 px-3">
                                    <ul className="news-tabs" role="tablist">
                                            <li className="news-tab-item" role="presentation">
                                                <button
                                                    className="news-tab-btn active fw-semibold"
                                                    id="feed-tab"
                                                    data-bs-toggle="pill"
                                                    data-bs-target="#yourFeed"
                                                    type="button"
                                                    role="tab"
                                                    aria-controls="yourFeed"
                                                    aria-selected="true"
                                                >
                                                Matching Sectors
                                                </button>
                                            </li>

                                        <li className="news-tab-item" role="presentation">
                                            <button
                                                className="news-tab-btn fw-semibold"
                                                id="all-tab"
                                                data-bs-toggle="pill"
                                                data-bs-target="#allNews"
                                                type="button"
                                                role="tab"
                                                aria-controls="allNews"
                                                aria-selected="false"
                                            >
                                                Matching Sources
                                            </button>
                                        </li>
                                    </ul>
                                    <div className="news-tab-content tab-content mt-4" id="newsTabContent">
                                            <div
                                                className="tab-pane fade show active"
                                                id="yourFeed"
                                                role="tabpanel"
                                                aria-labelledby="feed-tab"
                                            >
                                                <div className="row g-3">
                                                    {[...Array(3)].map((_, index)=>(
                                                        <div className="col" key={index}>
                                                            <div className="bg_glassEffect p-3">
                                                                <h5 className="text-white fw-semibold">Klimawandel</h5>
                                                                <div className=" news_detail d-flex align-items-center gap-2 pt-2">
                                                                    {sectors && sectors.slice(0, 2).map((sector, idx) => (
                                                                        <span key={idx} className='category text-white rounded-4 fw-light small px-3 py-1'>{sector}</span>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                    
                                                </div>
                                            </div>

                                        

                                        <div
                                            className="tab-pane fade"
                                            id="allNews"
                                            role="tabpanel"
                                            aria-labelledby="all-tab"
                                        >
                                            <div className="row g-3">
                                                {[...Array(3)].map((_, index) => (
                                                    <div className="col" key={index}>
                                                        <div className="bg_glassEffect p-3">
                                                            <h5 className="text-white fw-semibold">Klimawandel</h5>
                                                            <div className=" news_detail d-flex align-items-center gap-2 pt-2">
                                                                {sectors && sectors.slice(0, 2).map((sector, idx) => (
                                                                    <span key={idx} className='category text-white rounded-4 fw-light small px-3 py-1'>{sector}</span>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}

                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>*/}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReportView;
